import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import ForceGraph2D from "react-force-graph-2d";
import { Loader2, X, ArrowUpRight, Network } from "lucide-react";
import { Link } from "react-router-dom";

const TYPE_LABEL = {
  org: "ORGANIZATION",
  stakeholder_government: "GOV STAKEHOLDER",
  stakeholder_investor: "INVESTOR",
  stakeholder_client: "CLIENT",
  stakeholder_partner: "PARTNER",
  stakeholder_other: "STAKEHOLDER",
  project: "PROJECT",
  proposal: "PROPOSAL",
  meeting: "MEETING",
  briefing: "BRIEFING",
};

export default function Graph() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({});
  const [size, setSize] = useState({ w: 800, h: 600 });
  const wrapRef = useRef(null);
  const fgRef = useRef(null);

  useEffect(() => {
    api.get("/graph/network").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const measure = () => {
      if (wrapRef.current) {
        const r = wrapRef.current.getBoundingClientRect();
        setSize({ w: Math.max(600, r.width), h: Math.max(500, r.height) });
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [data]);

  const visibleData = useMemo(() => {
    if (!data) return { nodes: [], links: [] };
    const enabledTypes = Object.entries(filters).filter(([, v]) => v === false).map(([k]) => k);
    const hidden = new Set(enabledTypes);
    const nodes = data.nodes.filter((n) => !hidden.has(n.type));
    const ids = new Set(nodes.map((n) => n.id));
    const links = data.edges.filter((e) => ids.has(e.source) && ids.has(e.target));
    return { nodes, links };
  }, [data, filters]);

  const toggleType = (t) => setFilters((f) => ({ ...f, [t]: f[t] === false ? true : false }));

  const openNode = async (node) => {
    if (!node) return;
    try {
      const r = await api.get(`/graph/node/${encodeURIComponent(node.id)}`);
      setSelected({ ...node, detail: r.data });
    } catch {
      setSelected(node);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-slate-400 font-mono flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Mapping institutional intelligence…
      </div>
    );
  }
  if (!data) return <div className="p-10 text-slate-400">Failed to load graph.</div>;

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="graph-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Network className="w-3.5 h-3.5" style={{ color: "var(--gold-400)" }} strokeWidth={1.5} />
            <span className="label-eyebrow">// Institutional Intelligence Graph</span>
          </div>
          <h1 className="font-display text-3xl text-white tracking-tight">Relationship Network</h1>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">
            Live map of stakeholders, projects, proposals, meetings, and briefings — strength weighted by engagement.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 font-mono text-[11px] tracking-widest">
          <Chip label={`${data.stats.total_nodes} NODES`} />
          <Chip label={`${data.stats.total_edges} EDGES`} />
        </div>
      </div>

      {/* Filter legend */}
      <div className="flex flex-wrap gap-2" data-testid="graph-legend">
        {Object.entries(data.legend).map(([type, color]) => (
          <button
            key={type}
            onClick={() => toggleType(type)}
            data-testid={`graph-filter-${type}`}
            className={`flex items-center gap-2 px-3 py-1.5 border transition-all ${
              filters[type] === false ? "border-white/5 opacity-40" : "border-white/15 hover:border-white/30"
            }`}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: color }} />
            <span className="font-mono text-[10px] tracking-widest text-slate-300">{TYPE_LABEL[type] || type.toUpperCase()}</span>
            <span className="font-mono text-[10px] text-slate-500">{data.stats.by_type[type] || 0}</span>
          </button>
        ))}
      </div>

      {/* Graph + Inspector */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div
          ref={wrapRef}
          className="lg:col-span-3 nexus-glass-panel relative overflow-hidden"
          style={{ height: "72vh" }}
          data-testid="graph-canvas"
        >
          <ForceGraph2D
            ref={fgRef}
            graphData={visibleData}
            width={size.w}
            height={size.h}
            backgroundColor="rgba(5,11,20,0)"
            nodeLabel={(n) => `${n.label}${n.subtitle ? " · " + n.subtitle : ""}`}
            linkColor={() => "rgba(212,175,55,0.18)"}
            linkWidth={(l) => (l.weight || 1) * 0.4}
            linkDirectionalParticles={1}
            linkDirectionalParticleSpeed={0.004}
            linkDirectionalParticleColor={() => "rgba(212,175,55,0.55)"}
            nodeRelSize={5}
            nodeCanvasObject={(node, ctx, scale) => {
              // Guard: d3-force assigns x/y asynchronously; skip until layout populates.
              if (!Number.isFinite(node.x) || !Number.isFinite(node.y)) return;
              const radius = Math.max(2, (node.value || 6) / Math.max(1, scale * 0.4));
              // glow
              const grad = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, radius * 3);
              grad.addColorStop(0, node.color + "AA");
              grad.addColorStop(1, "rgba(0,0,0,0)");
              ctx.fillStyle = grad;
              ctx.beginPath();
              ctx.arc(node.x, node.y, radius * 3, 0, Math.PI * 2);
              ctx.fill();
              // core
              ctx.fillStyle = node.color;
              ctx.beginPath();
              ctx.arc(node.x, node.y, radius, 0, Math.PI * 2);
              ctx.fill();
              // label only at zoom-in
              if (scale > 1.3) {
                ctx.fillStyle = "#F8FAFC";
                ctx.font = `${10 / scale}px Inter`;
                ctx.textAlign = "center";
                ctx.fillText((node.label || "").slice(0, 24), node.x, node.y + radius + 8 / scale);
              }
            }}
            onNodeClick={openNode}
            cooldownTicks={120}
          />
        </div>

        {/* Inspector */}
        <aside className="lg:col-span-1 nexus-glass-panel p-5 max-h-[72vh] overflow-y-auto" data-testid="graph-inspector">
          {!selected ? (
            <div className="text-slate-400 text-sm">
              <div className="label-eyebrow mb-2">// Inspector</div>
              <p>Click any node to see its details, relationships, and pivot directly into the underlying record.</p>
            </div>
          ) : (
            <div>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                  <div className="label-eyebrow">{TYPE_LABEL[selected.type] || selected.type}</div>
                  <h2 className="font-display text-lg text-white mt-1 leading-tight">{selected.label}</h2>
                  {selected.subtitle && (
                    <div className="text-xs text-slate-400 mt-0.5">{selected.subtitle}</div>
                  )}
                </div>
                <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-white p-1">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-2 text-[12px]">
                {selected.stage && <KV k="Stage" v={selected.stage} />}
                {selected.status && <KV k="Status" v={selected.status} />}
                {selected.target && <KV k="Target" v={selected.target} />}
                {selected.money_ghs > 0 && <KV k="Value" v={`GH₵ ${(selected.money_ghs || 0).toLocaleString()}`} />}
                {selected.strength != null && <KV k="Strength" v={`${selected.strength}/100`} />}
                {selected.influence != null && <KV k="Influence" v={`${selected.influence}/100`} />}
                {selected.communication_count != null && (
                  <KV k="Communications" v={selected.communication_count} />
                )}
              </div>
              {selected.detail?.record?.notes && (
                <div className="mt-4 pt-4 border-t border-white/5">
                  <div className="label-eyebrow mb-1">// Notes</div>
                  <div className="text-[12px] text-slate-300 leading-relaxed">
                    {selected.detail.record.notes}
                  </div>
                </div>
              )}
              {selected.type.startsWith("stakeholder_") && (
                <Link to="/crm" className="mt-5 inline-flex items-center gap-1.5 text-[#D4AF37] hover:underline font-mono text-[10px] tracking-widest">
                  OPEN IN CRM <ArrowUpRight className="w-3 h-3" />
                </Link>
              )}
              {selected.type === "proposal" && (
                <Link to="/proposals" className="mt-5 inline-flex items-center gap-1.5 text-[#D4AF37] hover:underline font-mono text-[10px] tracking-widest">
                  OPEN PROPOSAL <ArrowUpRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

const Chip = ({ label }) => (
  <span className="px-2.5 py-1 border border-white/10 text-slate-300">{label}</span>
);

const KV = ({ k, v }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b border-white/5">
    <span className="label-eyebrow">{k}</span>
    <span className="text-white text-right text-[12px]">{v}</span>
  </div>
);
